import classNames from 'classnames';
import * as React from 'react';
import { PageSection } from '~/Features/Layouts/Components/PageSection/PageSection';
import { PageSectionHeading } from '~/Features/Layouts/Components/PageSectionHeading/PageSectionHeading';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { GobiStoryProps } from '~/Features/StudyPage/Campus/Components/GobiStory/GobiStory';
import { GobiStory } from '~/Features/StudyPage/Campus/Components/GobiStory/GobiStory';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { List } from '~/Partials/List/List';
import type { ImageBaseViewModel } from '~/Partials/Picture/ImageBaseViewModel.csharp';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import type { Theme } from '~/Utils/Theme';
import styles from './TestimoniesSection.module.css';
import type { TestimonySectionVariant } from './TestimoniesSectionVariant';
import type { TestimonyProps } from './Testimony/Testimony';
import { Testimony } from './Testimony/Testimony';

export interface TestimoniesSectionProps {
    id: string;
    testimonies: TestimonyProps[];
    heading?: HtmlString;
    imageFallback?: ImageBaseViewModel;
    gobiStory?: GobiStoryProps;
    variant?: TestimonySectionVariant;
    theme?: Theme;
}

export const TestimoniesSection: React.FC<TestimoniesSectionProps> = props => {
    const contextualTheme = useTheme();
    const { id, heading, testimonies, imageFallback, gobiStory, variant = 'dark', theme = contextualTheme } = props;

    const testimonyVariant = variant === 'dark' ? 'light' : 'dark';

    const renderMedia = () => {
        if (gobiStory) {
            return <GobiStory className={styles.Thumbnail} {...gobiStory} theme={theme} />;
        }

        if (imageFallback) {
            return (
                <Picture
                    className={styles.Thumbnail}
                    {...imageFallback}
                    profile={PictureProfiles.vertical}
                    theme={theme}
                />
            );
        }

        return <PicturePlaceholder className={styles.Thumbnail} theme={theme} />;
    };

    const renderTestimony = (testimony: TestimonyProps, index: number) => (
        <li className={styles.TestimonyItem} key={`${testimony.heading}-${index}`}>
            <Testimony {...testimony} variant={testimonyVariant} />
        </li>
    );

    return (
        <PageSection id={id} className={classNames(styles.TestimoniesSection, styles[`Variant--${variant}`])}>
            {heading && <PageSectionHeading heading={heading} className={styles.Heading} />}
            <div className={styles.Inner}>
                <div className={styles.Media}>{renderMedia()}</div>
                <List className={styles.Testimonies} items={testimonies} renderItem={renderTestimony} />
            </div>
        </PageSection>
    );
};
